"use client";

import Image from 'next/image';
import { Bell } from 'lucide-react';
import Icon from '@/app/components/atoms/Icon';
import TextLink from '@/app/components/atoms/TextLink';
import { event } from '@/lib/analytics/gtag';
import { isValidUrl } from '@/lib/utils';
import type { ApiBannerPayload } from '@/app/components/types';

interface PromotionalBannerProps {
  banner: ApiBannerPayload | null;
}

/**
 * @component PromotionalBanner
 * @description Hero promotional banner driven by WordPress banner API content.
 * Renders a background image with gradient overlay, optional notification bell icon,
 * optional category label above the headline, and a CTA link with arrow.
 * Returns null when no active banner is configured.
 * @param banner - Banner payload from /api/banner, or null for no banner.
 * @returns A banner section with background image, overlay, and CTA, or null.
 */
export default function PromotionalBanner({ banner }: PromotionalBannerProps) {
  if (!banner) return null;

  const { headline, subtitle, ctaText, ctaUrl, image, showNotificationIcon } = banner;

  return (
    <div className="relative overflow-hidden h-64 md:h-80 flex flex-col justify-end">
      {image ? (
        <Image
          src={image.url}
          alt={image.alt}
          fill
          className="object-cover"
          sizes="100vw"
        />
      ) : (
        <div className="absolute inset-0 bg-charcoal" aria-hidden="true" />
      )}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 60%)' }}
        aria-hidden="true"
      />
      {showNotificationIcon && (
        <Bell
          className="absolute top-[16px] left-[16px] md:top-[24px] md:left-[24px] z-10 text-white w-5 h-5"
          aria-hidden="true"
        />
      )}
      <div className="relative z-10 p-6 md:p-10">
        {subtitle && (
          <p className="text-label text-lime mb-2">{subtitle}</p>
        )}
        <h2 className="text-white text-display md:text-hero-mobile mb-4">
          {headline}
        </h2>
        {ctaText && ctaUrl && isValidUrl(ctaUrl) && (
          <TextLink
            as="a"
            href={ctaUrl}
            className="uppercase font-arquitecta text-base font-normal text-white hover:text-lime hover:no-underline"
            onClick={() => event('promotional_banner_cta_clicked', { category: 'navigation', label: headline })}
          >
            {ctaText}
            <Icon name="arrow" className="w-4 h-4 invert" />
          </TextLink>
        )}
      </div>
    </div>
  );
}
